import { useState } from 'react';
import SectionReveal from './SectionReveal';

/** Returns a display label for a news category key. */
function getCategoryLabel(key) {
  if (key === 'market') return 'Market';
  if (key === 'romania') return 'Romania';
  return 'KÖBER';
}

// Category top-line accent colours (language-agnostic, keyed by category field)
const CAT_ACCENT = {
  market:  { line: '#3B82F6', tag: 'rgba(59,130,246,0.75)',  tagBg: 'rgba(59,130,246,0.08)',  tagBorder: 'rgba(59,130,246,0.3)' },
  romania: { line: '#D4A017', tag: 'rgba(212,160,23,0.85)',  tagBg: 'rgba(212,160,23,0.06)',  tagBorder: 'rgba(212,160,23,0.3)' },
  kober:   { line: '#22C55E', tag: 'rgba(134,239,172,0.85)', tagBg: 'rgba(34,197,94,0.06)',   tagBorder: 'rgba(34,197,94,0.3)'  },
};

function NewsFilterTabs({ filters, active, onChange }) {
  return (
    <div className="flex flex-wrap gap-2 mb-14" data-testid="news-filters">
      {filters.map(({ key, label }) => (
        <button key={key} onClick={() => onChange(key)} data-testid={`news-filter-${key}`}
          className={`px-4 py-2 text-xs font-medium tracking-wide border transition-all duration-200 ${
            active === key
              ? 'bg-[#D4A017] border-[#D4A017] text-black'
              : 'border-white/20 text-white/50 hover:border-[#D4A017]/50 hover:text-white/80'
          }`}
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function NewsCard({ item, index }) {
  const accent = CAT_ACCENT[item.category] || CAT_ACCENT.kober;
  return (
    <SectionReveal delay={index * 90}>
      <div className="bg-[#0d0d0d] border border-white/8 hover:border-white/22 transition-all duration-300 h-full flex flex-col overflow-hidden group"
        data-testid={`news-card-${index}`}>
        {/* Top category accent line */}
        <div className="h-[2px] w-full" style={{ background: `linear-gradient(to right, ${accent.line}, transparent)` }} />
        <div className="p-7 flex flex-col flex-1">
          <div className="flex items-center gap-3 mb-5">
            <span className="inline-flex text-xs font-medium px-2.5 py-1 border tracking-wide"
              style={{ color: accent.tag, background: accent.tagBg, borderColor: accent.tagBorder }}>
              {item.tag}
            </span>
            <span className="text-xs text-white/30">{item.date}</span>
          </div>
          <h3 className="text-base font-medium text-white leading-snug mb-4 flex-1"
            style={{ fontFamily: "'Outfit', sans-serif" }}>
            {item.title}
          </h3>
          <p className="text-sm text-white/45 leading-relaxed">{item.body}</p>
        </div>
      </div>
    </SectionReveal>
  );
}

export default function NewsSection({ text }) {
  const [active, setActive] = useState('all');

  const filtered = active === 'all'
    ? text.items
    : text.items.filter(item => item.category === active);

  return (
    <section id="news" className="py-24 sm:py-32 bg-[#080808]">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <SectionReveal>
          <div className="mb-12">
            <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-4 font-medium">{text.label}</p>
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-0">
              <div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {text.title}
                </h2>
                <p className="text-base text-white/55 max-w-xl leading-relaxed">{text.subtitle}</p>
              </div>
              {/* Item count badge */}
              <div className="flex-shrink-0 text-right">
                <span className="text-4xl font-medium text-white/10 tabular-nums" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {String(filtered.length).padStart(2, '0')}
                </span>
                <div className="text-xs text-white/25 uppercase tracking-widest">
                  {active === 'all' ? 'articles' : 'results'}
                </div>
              </div>
            </div>
          </div>
        </SectionReveal>

        <SectionReveal delay={80}>
          <NewsFilterTabs filters={text.filters} active={active} onChange={setActive} />
        </SectionReveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((item, i) => (
            <NewsCard key={`${item.category}-${i}`} item={item} index={i} />
          ))}
        </div>

        {/* Legend */}
        <SectionReveal delay={200}>
          <div className="mt-16 pt-8 border-t border-white/6 flex flex-wrap gap-6">
            {Object.entries(CAT_ACCENT).map(([key, a]) => (
              <div key={key} className="flex items-center gap-2">
                <div className="w-4 h-0.5 rounded-full" style={{ background: a.line }} />
                <span className="text-xs text-white/30 capitalize">
                  {getCategoryLabel(key)}
                </span>
              </div>
            ))}
          </div>
        </SectionReveal>
      </div>
    </section>
  );
}
